import { useSliceSelection } from "@prismicio/editor-fields"
import {
  AnimatedElement,
  AnimatedList,
  Box,
  Gradient,
  HairlineButton,
  Icon,
  type RenderDragPreviewArgs,
  type RenderDropIndicatorArgs,
  Text,
} from "@prismicio/editor-ui"
import { type KeyboardEvent, type MouseEvent, useState } from "react"

import { useRepository } from "../../../app/services/repository"
import {
  trackAddSlice,
  trackDeleteSlice,
  trackSliceVariationChanged,
} from "../../../app/services/tracking"
import { useDocument, useDocumentReadOnly } from "../../DocumentProvider"
import { EmptySliceTableCard } from "./EmptySliceTableCard"
import { RefineSliceDialog } from "./RefineSliceDialog"
import {
  SelectSliceDialog,
  type SelectSliceDialogProps,
} from "./SelectSliceDialog"
import { SetUpPreviewCard } from "./SetUpPreviewCard"
import { Slice } from "./slice"
import {
  addSlice,
  addSlices,
  deleteSlice,
  getSectionSliceConfigs,
  getSectionSlices,
  getSharedSliceConfigs,
  getSliceTitle,
  isSharedSliceConfig,
  moveSlice,
  reorderSlices,
  replaceSlice,
  replaceVariation,
  type SectionSliceConfig,
  type VisualSharedSlice,
  type VisualSlice,
} from "./sliceTableData"
import { SmartCopyPasteDialog } from "./SmartCopyPasteDialog"
import { useSliceTableCopyPaste } from "./useCopyPaste"
import { trackSliceAdded } from "./utils/trackSliceAdded"
import { useGetSliceTrackingInfo } from "./utils/useGetSliceTrackingInfo"
import { useWhatsNewDialog, WhatsNewDialog } from "./WhatsNewDialog"

export function SliceTable() {
  const { data, sectionName, content, edit, customType } = useDocument()

  const readOnly = useDocumentReadOnly()

  const section = customType.json[sectionName]
  const slices = section ? getSectionSlices({ section, content }) : []

  const getSliceTrackingInfo = useGetSliceTrackingInfo()

  const [enableAnimations, setEnableAnimations] = useState(false)

  const [dialog, setDialog] = useState<Dialog>()
  const whatsNewDialog = useWhatsNewDialog()

  const selection = useSliceSelection()

  const sliceConfigs = section && getSectionSliceConfigs(section)
  const hasSliceConfigs = sliceConfigs && sliceConfigs.length !== 0

  const { copyPasteRef, canPaste, onCopy, onPaste } = useSliceTableCopyPaste({
    content,
    slices,
    sliceConfigs,
    edit,
    setEnableAnimations,
  })

  function onSingleOrMultiCopy(sliceKey: string) {
    if (selection.isSelected(sliceKey)) onCopy(selection.ids)
    else onCopy([sliceKey])
  }

  function updateSelection(
    sliceKey: string,
    fromEvent?: MouseEvent | KeyboardEvent,
  ) {
    selection.update({
      id: sliceKey,
      requestedFrom: "sliceTable",
      metaKey: fromEvent?.metaKey,
      shiftKey: fromEvent?.shiftKey,
      ids: slices.map((slice) => slice.key),
    })
  }

  function onMove(slice: VisualSlice, direction: "up" | "down") {
    edit(moveSlice(content, slice, direction))
  }

  function onDelete(slice: VisualSlice) {
    edit(deleteSlice(content, slice))
    trackDeleteSlice(getSliceTrackingInfo(slice))
  }

  function onStartAdd(atIndex: number) {
    setDialog({ name: "add", atIndex })
  }

  function onStartReplaceVariation(slice: VisualSlice) {
    setDialog({ name: "replaceVariation", slice })
  }

  function onStartRefine(slice: VisualSharedSlice) {
    setDialog({ name: "refine", slice })
  }

  function onStartSmartCopyPaste() {
    const index = dialog?.name === "add" ? dialog.atIndex : 0
    setDialog({ name: "smartCopyPaste", atIndex: index })
  }

  function onAdd(config: SectionSliceConfig, index: number) {
    setDialog(undefined)

    // Stagger dialog close animation -> Slice added animation so we can see both animations/side effects.
    setTimeout(() => {
      const { newContent, newSlice } = addSlice(content, config, index)

      edit(newContent)
      trackSliceAdded({ config, customType, data })
      updateSelection(newSlice.key)
    }, 200)
  }

  function onReplaceVariation(
    config: SectionSliceConfig,
    slice: VisualSharedSlice,
  ) {
    if (!isSharedSliceConfig(config)) return

    setDialog(undefined)

    edit(replaceVariation(content, config, slice))

    trackSliceVariationChanged({
      ...getSliceTrackingInfo(slice),
      variationName: slice.variation.name,
    })
  }

  function onSelectVariation(config: SectionSliceConfig) {
    if (!dialog) return

    if (dialog.name === "add") {
      return onAdd(config, dialog.atIndex)
    }

    if (dialog.name === "replaceVariation" && dialog.slice.type === "shared") {
      return onReplaceVariation(config, dialog.slice)
    }
  }

  function addPromptedSlice(args: {
    slice: VisualSharedSlice
    promptId: string
  }) {
    const { slice, promptId } = args

    setDialog(undefined)

    if (dialog?.name !== "add") return

    edit(addSlices({ content, slices: [slice], index: dialog.atIndex }))

    trackAddSlice({
      ...getSliceTrackingInfo(slice),
      promptId,
    })
  }

  function addRefinedSlice(args?: {
    promptId: string
    slice: VisualSharedSlice
  }) {
    setDialog(undefined)

    if (!args) return

    const { promptId, slice } = args

    const { newContent, newSlice } = replaceSlice({ content, slice })

    edit(newContent)
    updateSelection(newSlice.key)

    trackAddSlice({
      ...getSliceTrackingInfo(slice),
      promptId,
    })
  }

  function onFinishSmartCopyPaste(args?: { slices: VisualSharedSlice[] }) {
    setDialog(undefined)

    if (dialog?.name !== "smartCopyPaste") return
    if (!args) return

    const { slices } = args
    edit(addSlices({ content, slices, index: dialog.atIndex }))
  }

  function onReorder(slices: VisualSlice[]) {
    edit(reorderSlices(content, slices))
  }

  function renderDropIndicator(args: RenderDropIndicatorArgs) {
    const { isDropTarget, position, itemIndex } = args

    const marginTop = position === "after" ? 16 : 0
    const marginBottom = position === "before" ? 16 : 0

    return (
      <HairlineButton
        icon="add"
        dropMode={isDropTarget}
        sx={{ marginTop, marginBottom }}
        disabled={readOnly}
        onClick={() =>
          onStartAdd(position === "before" ? itemIndex : itemIndex + 1)
        }
      />
    )
  }

  function renderDragPreview(args: RenderDragPreviewArgs<VisualSlice>) {
    const { item } = args
    const { title, subTitle } = getSliceTitle(item)

    return (
      <Box
        padding={{ block: 8, inline: 12 }}
        backgroundColor="grey3"
        border
        borderRadius={6}
        alignItems="center"
        justifyContent="space-between"
        width={254}
      >
        <div>
          <Text variant="smallBold" color="grey11">
            {title}
          </Text>
          <Text variant="small" color="grey11">
            {subTitle}
          </Text>
        </div>
        <Icon name="dragIndicator" size="small" color="grey8" />
      </Box>
    )
  }

  const { simulatorUrl, isSupportedFramework } = useRepository()

  const shouldShowSetupPreviewCard =
    simulatorUrl === undefined && isSupportedFramework

  if (!hasSliceConfigs) return null

  return (
    <>
      <AnimatedElement ref={copyPasteRef} focusable>
        {slices.length === 0 ? (
          <EmptySliceTableCard
            key="empty"
            onAddFirstSlice={() => onStartAdd(0)}
          />
        ) : (
          <>
            {shouldShowSetupPreviewCard && (
              <Box padding={{ bottom: 16 }}>
                <SetUpPreviewCard onShowDialog={whatsNewDialog.open} />
              </Box>
            )}
            <AnimatedList
              ariaLabel="Slice Table"
              items={slices}
              getItemKey={getListItemKey}
              gap={16}
              animationDuration={enableAnimations ? 300 : 0}
              dnd={{
                onReorder,
                renderDragPreview,
                renderDropIndicator,
              }}
            >
              {(slice, index) => (
                <Slice
                  slice={slice}
                  index={index}
                  trackPreview={index === 0}
                  canPaste={canPaste}
                  onSelect={(event) => updateSelection(slice.key, event)}
                  onMoveUp={() => onMove(slice, "up")}
                  onMoveDown={() => onMove(slice, "down")}
                  onCopy={() => onSingleOrMultiCopy(slice.key)}
                  onPaste={() => onPaste(slice.key)}
                  onDelete={() => onDelete(slice)}
                  onReplaceVariation={() => onStartReplaceVariation(slice)}
                  onRefine={
                    slice.type === "shared"
                      ? () => onStartRefine(slice)
                      : undefined
                  }
                />
              )}
            </AnimatedList>
            <Gradient sx={{ position: "sticky", bottom: 0 }} />
          </>
        )}
      </AnimatedElement>

      <SelectSliceDialog
        open={dialog?.name === "add" || dialog?.name === "replaceVariation"}
        sliceConfigs={sliceConfigs}
        mode={getVariationDialogMode(dialog)}
        onSelect={onSelectVariation}
        onSelectPromptedSlice={addPromptedSlice}
        onSelectSmartCopyPaste={onStartSmartCopyPaste}
        onCloseRequest={() => setDialog(undefined)}
      />

      <RefineSliceDialog
        initialSlice={dialog?.name === "refine" ? dialog.slice : undefined}
        onFinish={addRefinedSlice}
      />

      <SmartCopyPasteDialog
        open={dialog?.name === "smartCopyPaste"}
        slices={getSharedSliceConfigs({ sliceConfigs })}
        onFinish={onFinishSmartCopyPaste}
      />

      <WhatsNewDialog dialog={whatsNewDialog} />
    </>
  )
}

type Dialog =
  | { name: "add"; atIndex: number }
  | { name: "replaceVariation"; slice: VisualSlice }
  | { name: "refine"; slice: VisualSharedSlice }
  | { name: "smartCopyPaste"; atIndex: number }

function getVariationDialogMode(
  dialog?: Dialog,
): SelectSliceDialogProps["mode"] {
  // Default to "add" if we don't know as radix Dialogs must always be evaluated, even when they're not opened.
  if (!dialog) return { name: "add" }

  switch (dialog?.name) {
    case "add":
    case "refine":
    case "smartCopyPaste":
      return { name: "add" }
    case "replaceVariation":
      // Replacing variations is only available for shared slices, as legacy slices do not have variations
      if (dialog.slice.type !== "shared")
        throw new Error("It is not possible to replace legacy slices")

      return {
        name: "replace",
        sliceId: dialog.slice.config.id,
      }
  }
}

// When replacing a slice variation, we want the overall card to keep the same key so that we don't play an exit then enter animation.
// UUIDs are unique across the document but are reused between the replaced and new slice.
function getListItemKey(slice: VisualSlice) {
  return slice.uuid
}
